<template>
    <div
        class="last:pb-(--beyond-safe-area-inset-bottom)"
        :class="{ 'h-full flex flex-col': useVirtualScroll }"
    >
        <FiltersBar
            v-if="filters.length > 1"
            :uuid="filterUuid"
            :filters="filters"
        />
        <div
            v-if="hasReadItems"
            class="flex items-center border-b border-neutral-subtle justify-between px-4 pb-4 shrink-0"
        >
            <DTabs
                type="switch"
                @selected="onTabSelected"
            >
                <DTab
                    name="All"
                    active
                />
                <DTab name="Unread" />
            </DTabs>

            <span
                v-if="readAllUrl"
                class="text-(--color-brand-700) text-sm font-medium border-b border-(--color-brand-700) mr-4"
                @click="readAll"
            >
                Mark all as read
            </span>
        </div>

        <div
            v-if="displaySearch"
            class="flex items-center justify-between pb-2 px-4 shrink-0"
            :class="{ 'pt-2': !hasReadItems }"
        >
            <DField id="Search">
                <DInput
                    v-model="search"
                    left-icon="magnifying-glass"
                    placeholder="Search"
                    aria-label="Search"
                    rounded
                />
            </DField>
        </div>

        <div
            v-if="searching || filterFetching || fetching"
            class="flex justify-center pt-[100px] shrink-0"
        >
            <UILoading class="w-12 h-12" />
        </div>

        <div
            v-if="!fetching && !searching && !filterFetching"
            ref="listContent"
            data-cy="list"
            :class="{ 'overflow-hidden flex flex-col h-auto w-full flex-1': useVirtualScroll, 'md:grid md:grid-cols-2': canShowMultipleColumnsOnTablet }"
        >
            <component
                :is="useVirtualScroll ? VirtualModuleComponents : ModuleComponents"
                :modules="filteredModules"
                :pagination-url="paginationUrl"
                :search-term="search"
                :search-properties="searchProperties"
                :is-remote-search="isRemoteSearch"
                @append-modules="appendModules"
            >
                <template #empty>
                    <template v-if="unread">
                        <Empty icon="empty-list">
                            <template #subtitle>
                                You have no unread updates.
                            </template>
                        </Empty>
                    </template>
                    <template v-else-if="filtering || search">
                        <!-- No items after filtering -->
                        <Empty icon="empty-search">
                            <template #title>
                                No result found.
                            </template>

                            <template #subtitle>
                                Try changing the filters or search term.
                            </template>
                        </Empty>
                    </template>
                    <template v-else>
                        <!-- No items -->
                        <slot name="no-items">
                            <Empty />
                        </slot>
                    </template>
                </template>
            </component>
        </div>

        <template v-if="headerMenuElement">
            <Teleport :to="headerMenuElement">
                <FiltersButton
                    v-if="filters.length"
                    :uuid="filterUuid"
                    :filters="filters"
                />
            </Teleport>

            <Teleport :to="headerMenuElement">
                <div
                    v-if="moreMenuItems.length"
                    :id="moreModalUuid"
                    class="w-[38px] h-[38px] flex items-center justify-center rounded-full"
                >
                    <PhIcon
                        icon="dots-three-vertical"
                        weight="bold"
                        :size="24"
                    />
                </div>
            </Teleport>

            <IonModal
                v-if="moreMenuItems.length"
                ref="modal"
                :trigger="moreModalUuid"
                :initial-breakpoint="1"
                :breakpoints="[0, 1]"
                class="ds-modal-bottom-sheet"
            >
                <div class="mt-6 mb-2 pb-(--beyond-safe-area-inset-bottom)">
                    <UISimple
                        v-for="(item, index) in moreMenuItems"
                        :key="`menu-item-${index}`"
                        :title="item.title"
                        :icon="{ icon: item.icon, icon_url: null, color: null }"
                        @click="openMoreMenuItem(item)"
                    />
                </div>
            </IonModal>
        </template>
    </div>
</template>

<script setup>
    import { useRouter } from 'vue-router'
    import {
        DTabs, DTab, DField, DInput,
    } from '@digistorm/spark'
    import { v4 as uuidv4 } from 'uuid'
    import {
        filter, debounce, keys, keyBy, every, isEmpty,
    } from 'lodash'
    import axios from 'axios'
    import PhIcon from '@/components/app/PhIcon.vue'
    import UISimple from '@/components/ui/ListItemLayouts/UISimple.vue'
    import ModuleComponents from '@/components/ModuleComponents.vue'
    import VirtualModuleComponents from '@/components/VirtualModuleComponents.vue'
    import Empty from '@/components/ui/Empty.vue'
    import FiltersButton from '@/components/filters/FiltersButton.vue'
    import FiltersBar from '@/components/filters/FiltersBar.vue'
    import { useFilterStore } from '@/stores'
    import { useFilters } from '@/composables'
    import componentTypes from '@/constants/componentTypes'

    const props = defineProps({
        config: Object,
    })

    const moduleHelpers = useModuleHelpers()
    const moduleStore = useModuleStore()
    const filterStore = useFilterStore()
    const { applyLocalFilter } = useFilters()
    const router = useRouter()

    const headerMenu = inject('headerMenu', null)
    const moduleId = inject('moduleId', ref(null))

    const { loadingId } = storeToRefs(moduleStore)

    const modal = ref(null)
    const searchFilter = ref(null)
    const readFilter = ref(null)

    const filterUuid = ref(null)
    const search = ref('')
    const searching = ref(false)
    const unread = ref(false)
    const listContent = ref(null)
    const moreModalUuid = ref(uuidv4())
    const filterFetching = ref(false)
    const listConfig = ref(null)
    const htmlTextCache = {}

    const paginationUrl = computed(() => listConfig.value.meta?.next)
    const useVirtualScroll = computed(() => listConfig.value.meta?.virtual_scroll)
    const hasReadItems = computed(() => listConfig.value.meta?.read_filter)
    const readAllUrl = computed(() => listConfig.value.meta?.read_multiple_url)
    const displaySearch = computed(() => !!(listConfig.value.meta?.search))
    const searchUrl = computed(() => listConfig.value.meta?.search?.url)
    const searchProperties = computed(() => listConfig.value.meta?.search?.properties)
    const searchType = computed(() => listConfig.value.meta?.search?.type)
    const isRemoteSearch = computed(() => searchType.value === 'remote')
    const moreMenuItems = computed(() => listConfig.value.meta?.menu_items ?? [])
    const filters = computed(() => listConfig.value.meta?.filters ?? [])
    const filtersById = computed(() => keyBy(filters.value, 'id'))

    const headerMenuElement = computed(() => {
        return headerMenu?.value?.$el?.querySelector('#header-menu')
    })

    const headerContextualElement = computed(() => {
        return headerMenu?.value?.$el?.querySelector('#header-contextual')
    })

    const activeFilters = computed(() => {
        return filterStore.filtersForUuid(filterUuid.value)
    })

    const filtering = computed(() => {
        return keys(activeFilters.value).length > 0
    })

    const localItemFilter = computed(() => {
        if (!filtering.value || listConfig.value.meta?.filter_url) {
            // Don't filter locally if:
            // - there are no selected filter options
            // - or all options selected
            // - or using remote filtering
            return null
        }

        return (item) => applyLocalFilter(filtersById.value, activeFilters.value, item.meta?.tags)
    })

    const combinedFilter = computed(() => {
        if (!localItemFilter.value && !readFilter.value && !searchFilter.value) {
            return null
        }
        return (item) => {
            return (localItemFilter.value?.(item) ?? true)
                && (readFilter.value?.(item) ?? true)
                && (searchFilter.value?.(item) ?? true)
        }
    })

    const module = computed(() => {
        return moduleStore.moduleFromId(moduleId.value)
    })

    const fetching = computed(() => {
        return loadingId.value
            && loadingId.value === moduleId.value
    })

    const filterModules = (modules) => {
        return flatMap(modules, (module) => {
            if (module.component !== componentTypes.GroupListItem) {
                return !combinedFilter.value || combinedFilter.value(module)
                    ? [module]
                    : []
            }

            const children = filterModules(module.items ?? [])
            if (!children.length) {
                return []
            }

            return [
                omit(module, 'items'),
                ...children,
            ]
        })
    }

    const filteredModules = computed(() => filterModules(listConfig.value.items ?? []))

    const canShowMultipleColumnsOnTablet = computed(() => {
        if (useVirtualScroll.value || isEmpty(filteredModules.value)) {
            return false
        }

        return every(filteredModules.value, (module) => {
            return module.component === componentTypes.GalleryListItem
                || (
                    module.component === componentTypes.ListItem
                    && module.data?.image
                    && module.data?.image_type === 'large'
                )
        })
    })

    const filtersChanged = (value, oldValue) => {
        if ((!oldValue && isEmpty(value)) || !listConfig.value.meta?.filter_url) {
            return
        }
        const requestFilters = filterStore.requestFiltersForUuid(filters.value, filterUuid.value)
        moduleStore.setQueryParams(moduleId.value, {
            ...(module.value?.params ?? {}),
            filters: requestFilters,
        })
        filterFetching.value = true
        moduleStore.loadContent(listConfig.value.meta?.filter_url, { filters: requestFilters })
            .then(({ data }) => {
                listConfig.value = data
                filterFetching.value = false
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    return
                }
                filterFetching.value = false
            })
    }

    const openMoreMenuItem = (item) => {
        moduleHelpers.handleClick(router, item)
            .catch((e) => {
                console.log('Failed to open link', e)
            })
            .finally(() => {
                modal.value.$el?.dismiss(null, 'cancel')
            })
    }

    const onTabSelected = (index) => {
        if (index === 0) {
            readFilter.value = null
            unread.value = false
        } else {
            unread.value = true
            readFilter.value = (item) => {
                return (item.data?.read ?? false) === false
            }
        }
    }

    const appendModules = (modules) => {
        listConfig.value.items.push(...modules.map((item) => {
            return {
                ...item,
                id: uuidv4(),
            }
        }))
    }

    const readAll = () => {
        const unreadItems = filter(listConfig.value.items, (item) => !item?.data?.read)
        each(unreadItems, (item) => {
            item.data.read = true
        })
        moduleStore.markMultipleAsRead(readAllUrl.value, map(unreadItems, 'meta.id'))
            .catch((error) => {
                console.log('Unable to mark all as read', error)
            })
    }

    const debounceSearch = debounce((searchVal) => {
        moduleStore.loadContent(
            searchUrl.value,
            searchVal.trim() ? { search: searchVal } : {},
        )
            .then(({ data }) => {
                listConfig.value = data
            })
            .finally(() => {
                searching.value = false
            })
    }, 600)

    const debounceSearchBaseModule = debounce((searchVal) => {
        moduleStore.setQueryParams(moduleId.value, {
            ...(module.value?.params ?? {}),
            search: searchVal,
        })
        moduleStore.loadModule(moduleId.value)
            .finally(() => {
                searching.value = false
            })
    }, 600)

    const htmlMatchesQuery = (html, lowerCaseQuery) => {
        if (htmlTextCache[html] === undefined) {
            const el = document.createElement('div')
            el.innerHTML = html
            htmlTextCache[html] = toLower(el.textContent ?? el.innerText ?? '')
        }

        return htmlTextCache[html].includes(lowerCaseQuery)
    }

    watch(search, (searchVal) => {
        const isRemote = searchType.value === 'remote'
        if (!searchVal && !isRemote) {
            searchFilter.value = null
            return
        }

        if (!isRemote) {
            const query = toLower(searchVal)
            searchFilter.value = (item) => {
                return some(searchProperties.value, (property) => {
                    return htmlMatchesQuery(get(item, property), query)
                })
            }
            return
        }

        searching.value = true
        if (searchUrl.value) {
            debounceSearch(searchVal)
        } else {
            debounceSearchBaseModule(searchVal)
        }
    })

    // Adding ids in watcher instead of computed property
    // Computed properties are recalculated in some situations and we don't want to generate new ids
    watch(() => props.config, (newVal) => {
        if (!newVal) {
            return
        }
        listConfig.value = newVal
    }, { immediate: true })

    watch(activeFilters, filtersChanged, { deep: true })

    onMounted(() => {
        moduleHelpers.setAnalyticsScreenFromConfig(listConfig.value)
        filterStore.initialiseFilters(filters.value, listConfig.value.meta?.id)
            .then((uuid) => {
                filterUuid.value = uuid
            })
    })

    onUnmounted(() => {
        if (filterUuid.value) {
            filterStore.clearFilters(filterUuid.value)
        }
    })
</script>
